import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import SiteHeader from "@/components/SiteHeader";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import PartnerLogos from "@/components/PartnerLogos";
import {
  ChevronRight, ShieldCheck, Lock, Headphones, Users, Gem, Smile, Star,
  Target, Eye, Heart, Check, MapPin, Tag, QrCode, Wallet, Clock,
  CreditCard, Award, ArrowRight
} from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/cwz7ddce_pp-aerea-ssm.png";
const CAVE_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/b9lemc03_FOTO_10.png";
const FAMILY_BEACH_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/x8ceycz8_pp-aerea-ssm.png";
const CARD_3D_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/uzxdncv1_card-pass-banner.png";

const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=80",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80",
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const handleBuy = () => navigate("/#passport");

  const WHY_ITEMS = [
    { Icon: MapPin, text: <><strong>{t("aboutPage.why1a")}</strong>{t("aboutPage.why1b")}</> },
    { Icon: Tag,    text: <><strong>{t("aboutPage.why2a")}</strong>{t("aboutPage.why2b")}</> },
    { Icon: QrCode, text: <><strong>{t("aboutPage.why3a")}</strong>{t("aboutPage.why3b")}</> },
    { Icon: Wallet, text: <><strong>{t("aboutPage.why4a")}</strong>{t("aboutPage.why4b")}</> },
    { Icon: Clock,  text: <><strong>{t("aboutPage.why5a")}</strong>{t("aboutPage.why5b")}</> },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
      <Seo
        title={language === "en" ? "About PASS Algarve | Who We Are" : "Sobre o PASS Algarve | Quem Somos"}
        description={language === "en"
          ? "Get to know PASS Algarve: the platform that brings together the best attractions, tours and experiences in Lagos and the Algarve in a single digital pass with QR Code."
          : "Conheça o PASS Algarve: a plataforma que reúne as melhores atrações, passeios e experiências de Lagos e do Algarve num único passe digital com QR Code."}
      />
      <SiteHeader active={t("nav.about")} onBuyClick={handleBuy} />

      {/* ============== HERO ============== */}
      <section className="relative pt-20" data-testid="about-hero">
        <div className="relative h-[460px] lg:h-[520px] overflow-hidden bg-gradient-to-br from-[#071A35] via-[#0a2547] to-[#13254D]">
          <img
            src={HERO_BG}
            alt="Algarve"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071A35] via-[#071A35]/70 to-[#071A35]/30" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <nav className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-6" data-testid="about-breadcrumb">
              <Link to="/" className="hover:text-[#D4A85D]">{t("aboutPage.home")}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#D4A85D] font-semibold">{t("aboutPage.breadcrumbAbout")}</span>
            </nav>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-white max-w-2xl">
              <p className="text-white text-2xl lg:text-3xl font-bold tracking-tight">{t("aboutPage.heroPre")}</p>
              <h1 className="text-5xl lg:text-6xl font-bold text-[#D4A85D] leading-[1] tracking-tight mb-5">PASS ALGARVE</h1>
              <p className="text-white/85 text-base lg:text-lg leading-relaxed mb-8">
                {t("aboutPage.heroDesc")}
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {AVATARS.map((src, i) => (
                    <img key={i} src={src} alt="" className="w-11 h-11 rounded-full border-2 border-white/40 object-cover" />
                  ))}
                </div>
                <p className="text-white/85 text-sm leading-snug max-w-xs">
                  <span className="font-bold text-white">{t("aboutPage.travelersBold")}</span> {t("aboutPage.travelersRest")}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============== QUEM SOMOS ============== */}
      <section className="py-16 lg:py-20 bg-white" data-testid="who-we-are">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          {/* LEFT */}
          <div>
            <p className="text-[#D4A85D] text-xs font-bold uppercase tracking-[0.2em] mb-4">{t("aboutPage.whoLabel")}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#071A35] leading-tight mb-6">
              {t("aboutPage.whoTitle")}
            </h2>
            <p className="text-[#071A35]/75 text-base leading-relaxed mb-4">
              {t("aboutPage.whoP1a")}<span className="text-[#D4A85D] font-semibold">{t("aboutPage.whoP1b")}</span>{t("aboutPage.whoP1c")}
            </p>
            <p className="text-[#071A35]/75 text-base leading-relaxed mb-8">
              {t("aboutPage.whoP2")}
            </p>

            <div className="grid grid-cols-3 gap-3">
              {[
                { Icon: Award,      title: t("aboutPage.f1t"), sub: t("aboutPage.f1s") },
                { Icon: Lock,       title: t("aboutPage.f2t"), sub: t("aboutPage.f2s") },
                { Icon: Headphones, title: t("aboutPage.f3t"), sub: t("aboutPage.f3s") },
              ].map((f, i) => (
                <div key={i} className="bg-[#F8F4EB] rounded-2xl p-4 text-center border border-[#D4A85D]/15">
                  <div className="w-10 h-10 mx-auto rounded-full border border-[#D4A85D]/40 bg-[#D4A85D]/10 flex items-center justify-center mb-2">
                    <f.Icon className="w-4 h-4 text-[#D4A85D]" />
                  </div>
                  <p className="text-[#071A35] text-xs font-bold leading-tight">{f.title}</p>
                  <p className="text-[#071A35]/60 text-xs">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="relative">
            <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#071A35] via-[#0a2547] to-[#D4A85D]">
              <img
                src={CAVE_IMAGE}
                alt="Algarve cave"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <div className="absolute bottom-5 right-5 bg-[#071A35] border border-[#D4A85D]/40 rounded-xl px-5 py-3 flex items-center gap-3 shadow-xl">
              <ShieldCheck className="w-5 h-5 text-[#D4A85D]" />
              <div>
                <p className="text-white font-bold text-sm leading-tight">{t("aboutPage.oneYear")}</p>
                <p className="text-white/70 text-[10px] uppercase tracking-widest">{t("aboutPage.ofValidity")}</p>
              </div>
              <ShieldCheck className="w-5 h-5 text-[#D4A85D]" />
            </div>
          </div>
        </div>
      </section>

      {/* ============== POR QUE ESCOLHER ============== */}
      <section className="py-16 lg:py-20 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          {/* LEFT: image with badge */}
          <div className="relative">
            <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#D4A85D] via-[#0a2547] to-[#071A35]">
              <img
                src={FAMILY_BEACH_IMAGE}
                alt="Algarve"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <div className="absolute bottom-6 left-6 bg-[#071A35] border border-[#D4A85D]/40 rounded-xl px-5 py-3 flex items-center gap-3 shadow-xl max-w-xs">
              <Award className="w-9 h-9 text-[#D4A85D] flex-shrink-0" strokeWidth={1.4} />
              <div>
                <p className="text-white font-bold text-sm leading-tight uppercase tracking-wider">{t("aboutPage.awardTitle")}</p>
                <p className="text-white/70 text-[10px] uppercase tracking-widest mt-0.5">{t("aboutPage.awardSub")}</p>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div>
            <p className="text-[#D4A85D] text-xs font-bold uppercase tracking-[0.2em] mb-3">{t("aboutPage.whyLabel")}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#071A35] leading-tight mb-8">
              {t("aboutPage.whyTitle")}
            </h2>

            <ul className="space-y-4">
              {WHY_ITEMS.map((w, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`why-item-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-[#D4A85D]/10 border border-[#D4A85D]/30 flex items-center justify-center flex-shrink-0">
                    <w.Icon className="w-4 h-4 text-[#D4A85D]" />
                  </div>
                  <p className="text-[#071A35]/85 text-base leading-relaxed pt-1">{w.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-12 bg-[#F8F4EB]" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#071A35] rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#D4A85D]/10 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              {/* card image */}
              <div className="lg:col-span-3 flex justify-center">
                <img src={CARD_3D_IMAGE} alt="PASS Algarve" className="w-full max-w-[220px] drop-shadow-[0_25px_60px_rgba(212,168,93,0.45)]" />
              </div>

              {/* text + buttons */}
              <div className="lg:col-span-6 text-white">
                <h2 className="text-2xl lg:text-3xl font-bold mb-3 uppercase">{t("aboutPage.ctaTitle")}</h2>
                <p className="text-white/75 text-sm leading-relaxed mb-6">
                  {t("aboutPage.ctaDesc")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleBuy}
                    className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-7 py-6 text-sm uppercase tracking-wider"
                    data-testid="about-cta-buy"
                  >
                    {t("aboutPage.ctaBuy")}
                  </Button>
                  <Link to="/#passport">
                    <Button variant="outline" className="border-2 border-white/40 bg-transparent text-white hover:bg-white/10 font-bold rounded-md px-7 py-6 text-sm uppercase tracking-wider">
                      {t("aboutPage.ctaView")}
                    </Button>
                  </Link>
                </div>
              </div>

              {/* mini features */}
              <div className="lg:col-span-3 space-y-3">
                {[
                  { Icon: Lock,       title: t("aboutPage.mini1t"), sub: t("aboutPage.mini1s") },
                  { Icon: CreditCard, title: t("aboutPage.mini2t"), sub: t("aboutPage.mini2s") },
                  { Icon: Headphones, title: t("aboutPage.mini3t"), sub: t("aboutPage.mini3s") },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 text-white">
                    <div className="w-9 h-9 rounded-full bg-[#D4A85D]/10 border border-[#D4A85D]/30 flex items-center justify-center flex-shrink-0">
                      <f.Icon className="w-4 h-4 text-[#D4A85D]" />
                    </div>
                    <div>
                      <p className="font-bold text-[11px] uppercase tracking-wider">{f.title}</p>
                      <p className="text-white/60 text-[11px] leading-snug">{f.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PARTNERS ============== */}
      <section className="py-14 bg-white" data-testid="about-partners">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-[#071A35] font-bold uppercase tracking-[0.15em] text-sm mb-2">{t("aboutPage.partnersTitle")}</h3>
          <div className="w-12 h-0.5 bg-[#D4A85D] mx-auto mb-8" />
          <PartnerLogos className="mb-10" />
          <Link to="/#partners">
            <Button variant="outline" className="border-2 border-[#D4A85D] text-[#071A35] hover:bg-[#D4A85D] hover:text-[#071A35] font-bold rounded-md px-8 py-5 text-xs uppercase tracking-wider" data-testid="about-partners-cta">
              {t("aboutPage.partnersCta")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
